import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Copy, Mail, MessageCircle, Plus, Search, Send, Sparkles, Trash2, UserPlus } from 'lucide-react';
import { Modal } from '../../components/Modal/Modal';
import { useToast } from '../../components/Toast';
import { Avatar } from '../../components/Planung/Avatar';
import {
  ANMELDE_DOMAIN, ANTWORT_INFO, SICHT_INFO, anfrageEntfernen, feedbackAnfragen, feedbackLink, inZwischenablage, istRandoriAdresse, listePersonen,
  mailtoLink, personEinladen, personSpeichern, personenVorschlaege, teilenText, wannText, whatsappLink,
  type Ansprechperson, type PersonVorschlag, type PlanungsMail,
} from '../../lib/planung';
import './Dialoge.css';

// Wie die Prüfung der Tabelle (Mail_ansprechpersonen.email)
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

type Props = {
  mail: Pick<PlanungsMail, 'id' | 'name' | 'betreff' | 'sportschulen' | 'anfragen'>;
  offen: boolean;
  onClose: () => void;
  onGeaendert: () => void;
};

/**
 * Ansprechpersonen um Feedback zu einer Mail bitten: auswählen oder neu
 * anlegen (auch aus dem CRM vorgeschlagen), dann den persönlichen Link
 * kopieren, per WhatsApp/Mail teilen oder die Einladung aus dem Mailtool
 * schicken. Rückmeldungen landen unter „Geplante Mails".
 */
export function AnfrageDialog({ mail, offen, onClose, onGeaendert }: Props) {
  const toast = useToast();
  const qc = useQueryClient();
  const [suche, setSuche] = useState('');
  const [auswahl, setAuswahl] = useState<Set<string>>(new Set());
  const [nachricht, setNachricht] = useState('');
  const [neu, setNeu] = useState<{ name: string; email: string; rolle_text: string } | null>(null);
  const [arbeitet, setArbeitet] = useState(false);
  const [kopiert, setKopiert] = useState<string | null>(null);

  const personen = useQuery({ queryKey: ['ansprechpersonen'], queryFn: listePersonen, enabled: offen });
  const vorschlaege = useQuery({ queryKey: ['personen-vorschlaege', mail.id], queryFn: () => personenVorschlaege(mail.id), enabled: offen, staleTime: 300_000 });

  const zugeordnet = useMemo(() => new Set(mail.anfragen.map((a) => a.person_id)), [mail.anfragen]);
  const personVon = useMemo(() => new Map((personen.data ?? []).map((p) => [p.id, p])), [personen.data]);
  const waehlbar = useMemo(() => {
    const s = suche.trim().toLowerCase();
    return (personen.data ?? [])
      .filter((p) => p.aktiv && !zugeordnet.has(p.id))
      .filter((p) => !s || [p.name, p.email ?? '', p.rolle_text ?? '', ...p.sportschulen].some((x) => x.toLowerCase().includes(s)));
  }, [personen.data, suche, zugeordnet]);
  const neueVorschlaege = useMemo(() => {
    const vorhanden = new Set((personen.data ?? []).map((p) => (p.email ?? '').toLowerCase()).filter(Boolean));
    // Nur Vorschläge mit Adresse @randori-pro.de — andere lassen sich nicht anlegen.
    return (vorschlaege.data ?? [])
      .filter((v) => istRandoriAdresse(v.email) && !vorhanden.has((v.email ?? '').trim().toLowerCase()))
      .slice(0, 8);
  }, [vorschlaege.data, personen.data]);

  function umschalten(id: string) {
    setAuswahl((alt) => {
      const n = new Set(alt);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  }

  async function neuLaden() {
    await qc.invalidateQueries({ queryKey: ['ansprechpersonen'] });
    onGeaendert();
  }

  async function personAnlegen(daten: Partial<Ansprechperson> & { name: string }) {
    const adresse = daten.email?.trim() ?? '';
    if (!adresse || !EMAIL_RE.test(adresse)) {
      toast.error(`Bitte gib eine vollständige Adresse @${ANMELDE_DOMAIN} ein — ohne sie kann die Person den Link nicht öffnen.`);
      return;
    }
    if (!istRandoriAdresse(adresse)) {
      toast.error(`Nur Adressen @${ANMELDE_DOMAIN}: Feedback-Links gibt es nur für Randori-Pro-Konten.`);
      return;
    }
    setArbeitet(true);
    try {
      const p = await personSpeichern(daten);
      await qc.invalidateQueries({ queryKey: ['ansprechpersonen'] });
      setAuswahl((alt) => new Set(alt).add(p.id));
      setNeu(null);
      toast.success(`${p.name} angelegt`);
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setArbeitet(false);
    }
  }

  function ausVorschlag(v: PersonVorschlag) {
    const gf = /gesch/i.test(v.rolle_text ?? '');
    void personAnlegen({
      name: v.name, email: v.email, rolle_text: v.rolle_text,
      // Studioleitung sieht die Mails ihrer Sportschulen, Geschäftsführung alle.
      sicht: gf ? 'alle' : v.sportschulen.length ? 'sportschulen' : 'zugeordnet',
      sportschulen: v.sportschulen,
    });
  }

  async function anfragen() {
    if (auswahl.size === 0) return;
    setArbeitet(true);
    try {
      await feedbackAnfragen(mail.id, [...auswahl], nachricht);
      toast.success(auswahl.size === 1 ? 'Feedback angefragt' : `${auswahl.size} Personen angefragt`);
      setAuswahl(new Set());
      await neuLaden();
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setArbeitet(false);
    }
  }

  async function entfernen(personId: string, name: string) {
    if (!window.confirm(`${name} nicht mehr um Feedback zu dieser Mail bitten? Bisherige Kommentare bleiben stehen.`)) return;
    try {
      await anfrageEntfernen(mail.id, personId);
      await neuLaden();
    } catch (err) {
      toast.error((err as Error).message);
    }
  }

  async function kopieren(p: Ansprechperson) {
    const ok = await inZwischenablage(feedbackLink(p.token, mail.id));
    if (ok) { setKopiert(p.id); setTimeout(() => setKopiert(null), 2000); toast.success('Link kopiert'); }
    else toast.error('Kopieren ging nicht — bitte den Link von Hand markieren.');
  }

  async function einladen(p: Ansprechperson) {
    setArbeitet(true);
    try {
      await personEinladen(p.id, [mail.id], nachricht || null);
      toast.success(`Einladung an ${p.name} verschickt`);
      await neuLaden();
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setArbeitet(false);
    }
  }

  const betreff = mail.betreff || mail.name;

  return (
    <Modal isOpen={offen} onClose={onClose} title="Feedback anfragen" size="lg">
      <div className="gd-form">
        <p className="gd-betreff">{betreff}</p>

        <section className="gd-abschnitt" aria-labelledby="gd-gefragt">
          <h3 id="gd-gefragt" className="gd-titel">Gefragt ({mail.anfragen.length})</h3>
          {mail.anfragen.length === 0 && <p className="gd-leer">Noch niemand. Wähle unten Personen aus — jede bekommt ihren eigenen Link.</p>}
          <ul className="gd-liste">
            {mail.anfragen.map((a) => {
              const p = personVon.get(a.person_id);
              // Gesperrte Links nicht mehr zum Teilen anbieten.
              const teilbar = !!p && p.aktiv;
              const link = p ? feedbackLink(p.token, mail.id) : '';
              return (
                <li key={a.person_id} className="gd-person">
                  <Avatar name={a.name} groesse="md" />
                  <div className="gd-person-text">
                    <div className="gd-person-name">{a.name} <span className={`badge ${ANTWORT_INFO[a.antwort].badge}`}>{ANTWORT_INFO[a.antwort].label}</span></div>
                    <div className="gd-person-sub">
                      {p?.rolle_text ? `${p.rolle_text} · ` : ''}
                      {a.gesehen_am ? `zuletzt geöffnet ${wannText(a.gesehen_am)}` : 'noch nicht geöffnet'}
                      {a.eingeladen_am ? ` · eingeladen ${wannText(a.eingeladen_am)}` : ''}
                    </div>
                    {teilbar && <input className="gd-link" readOnly value={link} aria-label={`Link für ${a.name}`} onFocus={(e) => e.currentTarget.select()} />}
                    {p && !p.aktiv && <span className="gd-person-sub gd-gesperrt">Link gesperrt — unter „Ansprechpersonen“ wieder freigeben</span>}
                    {p && p.aktiv && !istRandoriAdresse(p.email) && (
                      <span className="gd-person-sub gd-ohne-mail">Ohne Adresse @{ANMELDE_DOMAIN} kann die Person den Link nicht öffnen — unter „Ansprechpersonen“ eine eintragen.</span>
                    )}
                  </div>
                  {p && (
                    <div className="gd-person-aktionen">
                      {teilbar && (
                        <>
                      <button type="button" className="btn btn-secondary btn-sm" onClick={() => void kopieren(p)} title="Link kopieren">
                        {kopiert === p.id ? <Check size={14} /> : <Copy size={14} />} Link
                      </button>
                      <a className="btn btn-ghost btn-sm" href={whatsappLink(teilenText(p.name, betreff, link))} target="_blank" rel="noreferrer" title="Per WhatsApp teilen">
                        <MessageCircle size={14} /> <span className="gd-nur-breit">WhatsApp</span>
                      </a>
                      {p.email && (
                        <>
                          <a className="btn btn-ghost btn-sm" href={mailtoLink(p.email, `Feedback: ${betreff}`, teilenText(p.name, betreff, link))} title="Im eigenen Mailprogramm öffnen">
                            <Mail size={14} /> <span className="gd-nur-breit">Mail</span>
                          </a>
                          <button type="button" className="btn btn-ghost btn-sm" disabled={arbeitet} onClick={() => void einladen(p)} title="Einladung aus dem Mailtool schicken">
                            <Send size={14} /> <span className="gd-nur-breit">Einladen</span>
                          </button>
                        </>
                      )}
                        </>
                      )}
                      <button type="button" className="btn btn-ghost btn-sm" onClick={() => void entfernen(a.person_id, a.name)} title="Nicht mehr fragen" aria-label={`${a.name} entfernen`}>
                        <Trash2 size={14} />
                      </button>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </section>

        <section className="gd-abschnitt" aria-labelledby="gd-dazu">
          <h3 id="gd-dazu" className="gd-titel">Weitere Personen fragen</h3>
          <div className="search-input-wrapper gd-suche">
            <Search size={15} aria-hidden="true" />
            <input className="search-input" placeholder="Name, Rolle oder Sportschule" value={suche} onChange={(e) => setSuche(e.target.value)} aria-label="Personen suchen" />
          </div>
          {personen.isLoading && <div className="loading-spinner" style={{ margin: '1rem auto' }} />}
          {personen.error && <p className="gd-fehler">{(personen.error as Error).message}</p>}
          <ul className="gd-auswahl">
            {waehlbar.map((p) => (
              <li key={p.id}>
                <label className={`gd-waehlen${auswahl.has(p.id) ? ' aktiv' : ''}`}>
                  <input type="checkbox" checked={auswahl.has(p.id)} onChange={() => umschalten(p.id)} />
                  <Avatar name={p.name} groesse="sm" />
                  <span className="gd-waehlen-text">
                    <strong>{p.name}</strong>
                    <span>{[p.rolle_text, SICHT_INFO[p.sicht].label].filter(Boolean).join(' · ')}</span>
                  </span>
                </label>
              </li>
            ))}
          </ul>
          {!personen.isLoading && waehlbar.length === 0 && !neu && <p className="gd-leer">{suche ? 'Niemand gefunden.' : 'Alle angelegten Personen sind schon gefragt.'}</p>}

          {neueVorschlaege.length > 0 && (
            <div className="gd-vorschlaege">
              <span className="gd-vorschlaege-titel"><Sparkles size={14} aria-hidden="true" /> Aus dem CRM</span>
              {neueVorschlaege.map((v) => (
                <button key={`${v.name}-${v.email}`} type="button" className="chip" disabled={arbeitet} onClick={() => ausVorschlag(v)} title={v.email ?? ''}>
                  <Plus size={13} aria-hidden="true" /> {v.name}{v.rolle_text ? ` · ${v.rolle_text}` : ''}
                </button>
              ))}
            </div>
          )}

          {neu ? (
            <div className="gd-neu">
              <div className="grid-3 gd-reihe">
                <div>
                  <label className="form-label" htmlFor="gd-neu-name">Name</label>
                  <input id="gd-neu-name" className="form-input" value={neu.name} maxLength={120} autoFocus onChange={(e) => setNeu({ ...neu, name: e.target.value })} />
                </div>
                <div>
                  <label className="form-label" htmlFor="gd-neu-mail">E-Mail @{ANMELDE_DOMAIN}</label>
                  <input
                    id="gd-neu-mail" className="form-input" type="email" required value={neu.email} placeholder={`vorname.nachname@${ANMELDE_DOMAIN}`}
                    aria-invalid={EMAIL_RE.test(neu.email.trim()) && !istRandoriAdresse(neu.email) ? true : undefined}
                    onChange={(e) => setNeu({ ...neu, email: e.target.value })}
                  />
                </div>
                <div>
                  <label className="form-label" htmlFor="gd-neu-rolle">Rolle (optional)</label>
                  <input id="gd-neu-rolle" className="form-input" value={neu.rolle_text} maxLength={80} placeholder="z. B. Trainer Spandau" onChange={(e) => setNeu({ ...neu, rolle_text: e.target.value })} />
                </div>
              </div>
              <p className="form-hint">Die Person sieht nur die Mails, zu denen Du sie fragst. Mehr freigeben kannst Du im Reiter „Ansprechpersonen". Öffnen und kommentieren kann sie den Link nach der Anmeldung mit dem Google-Konto dieser Adresse — Links gibt es nur für Adressen @{ANMELDE_DOMAIN}.</p>
              {EMAIL_RE.test(neu.email.trim()) && !istRandoriAdresse(neu.email) && (
                <p className="form-hint gd-ohne-mail" role="note">Nur Adressen @{ANMELDE_DOMAIN} — mit dieser Adresse lässt sich die Person nicht anlegen.</p>
              )}
              <div className="form-actions">
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => setNeu(null)}>Abbrechen</button>
                <button type="button" className="btn btn-secondary btn-sm" disabled={arbeitet || !neu.name.trim() || !istRandoriAdresse(neu.email)}
                  onClick={() => void personAnlegen({ name: neu.name, email: neu.email || null, rolle_text: neu.rolle_text || null, sicht: 'zugeordnet', sportschulen: [] })}>
                  <UserPlus size={14} /> Anlegen und auswählen
                </button>
              </div>
            </div>
          ) : (
            <button type="button" className="btn btn-ghost btn-sm gd-neu-knopf" onClick={() => setNeu({ name: '', email: '', rolle_text: '' })}>
              <UserPlus size={14} /> Neue Person
            </button>
          )}

          <label className="form-label" htmlFor="gd-nachricht">Worum geht es? (optional)</label>
          <textarea id="gd-nachricht" className="form-textarea" rows={2} maxLength={1500} value={nachricht} onChange={(e) => setNachricht(e.target.value)}
            placeholder="z. B. Bitte die Uhrzeiten und den Treffpunkt prüfen" />
          <p className="form-hint">Steht über der Mail, wenn die Person den Link öffnet, und in der Einladung.</p>
        </section>

        <div className="form-actions">
          <button type="button" className="btn btn-ghost" onClick={onClose}>Schließen</button>
          <button type="button" className="btn btn-primary" disabled={arbeitet || auswahl.size === 0} onClick={() => void anfragen()}>
            {auswahl.size > 1 ? `${auswahl.size} Personen fragen` : 'Feedback anfragen'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
